import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Input, Tag, Spin, Typography, theme } from 'antd';
import { SearchOutlined, EnterOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../shared/services/apiClient';
import { adminPath } from '../../shared/config/paths';
import { t } from '../../shared/i18n';
import { flattenMenu } from '../layouts/adminMenu';

const { Text } = Typography;

const TYPE_LABEL = {
    user: '用户', cdn_domain: 'CDN 域名', cdn_hostname: 'CDN 主机名', dns_zone: 'DNS 区域',
    reg_domain: '注册域名', vm: '云服务器', ip: 'IP', invoice: '账单', subscription: '订阅',
    ticket: '工单', cdn_node: 'CDN 节点', node: '节点',
};

const STATUS_COLOR = (s) => {
    const v = String(s || '').toLowerCase();
    if (['active', 'paid', 'online', 'running', 'enable', 'enabled', 'healthy', 'up', 'ok'].includes(v)) return 'green';
    if (['pending', 'unpaid', 'warning', 'open', 'partial', 'degraded'].includes(v)) return 'gold';
    if (['failed', 'error', 'suspended', 'overdue', 'offline', 'down', 'banned', 'disable', 'disabled'].includes(v)) return 'red';
    if (['processing', 'provisioning', 'answered', 'in_progress'].includes(v)) return 'blue';
    return 'default';
};

const PAGES = flattenMenu();

/** 页面模糊匹配:每个词都要命中;包含 > 子序列(只在英文名里、跨度不超过词长 2 倍,如 "cdnnod" → CDN nodes) */
const fuzzyIn = (text, w) => {
    for (let start = text.indexOf(w[0]); start !== -1; start = text.indexOf(w[0], start + 1)) {
        let k = 0;
        let j = start;
        for (; j < text.length && k < w.length && j - start < w.length * 2; j++) if (text[j] === w[k]) k++;
        if (k === w.length) return true;
    }
    return false;
};
const scorePage = (page, q) => {
    const label = page.tKey ? t(page.zh) : page.zh;
    const hay = `${label} ${page.zh} ${page.en} ${page.path} ${page.group}`.toLowerCase();
    const en = page.en.toLowerCase();
    const words = q.split(/\s+/).filter(Boolean);
    let score = 0;
    let direct = true;
    for (const w of words) {
        const i = hay.indexOf(w);
        if (i === 0) score += 30;
        else if (i > 0) score += 20 - Math.min(i, 15) / 3;
        else if (w.length >= 3 && fuzzyIn(en, w)) { score += 3; direct = false; }
        else return { score: 0, direct: false };
    }
    if (label.toLowerCase() === q || en === q) score += 50;
    return { score, direct };
};

const isEntityQuery = (q) => /^#?\d+$/.test(q) || q.length >= 2;

const CommandPalette = ({ open, onClose, recent = [] }) => {
    const navigate = useNavigate();
    const { token } = theme.useToken();
    const [q, setQ] = useState('');
    const [entities, setEntities] = useState([]);
    const [loading, setLoading] = useState(false);
    const [active, setActive] = useState(0);
    const inputRef = useRef(null);
    const listRef = useRef(null);
    const reqId = useRef(0);

    useEffect(() => {
        if (open) {
            setQ('');
            setEntities([]);
            setActive(0);
            setTimeout(() => inputRef.current && inputRef.current.focus(), 50);
        }
    }, [open]);

    const query = q.trim().toLowerCase();

    const pages = useMemo(() => {
        if (!query) {
            return recent.map((r) => ({ kind: 'page', direct: true, path: r.path, title: r.label, sub: '最近访问' }));
        }
        return PAGES.map((p) => ({ p, ...scorePage(p, query) }))
            .filter((x) => x.score > 0)
            .sort((a, b) => b.score - a.score)
            .slice(0, 8)
            .map(({ p, direct }) => ({ kind: 'page', direct, path: p.path, title: p.tKey ? t(p.zh) : p.zh, sub: p.group }));
    }, [query, recent]);

    // 实体搜索:防抖 250ms,只用最后一次请求的结果
    useEffect(() => {
        const raw = q.trim();
        if (!open || !isEntityQuery(raw)) {
            setEntities([]);
            setLoading(false);
            return undefined;
        }
        const id = ++reqId.current;
        setLoading(true);
        const timer = setTimeout(() => {
            apiClient.get('/api/admin/search', { params: { q: raw } })
                .then((res) => {
                    if (id !== reqId.current) return;
                    const items = res?.data?.data?.items || [];
                    setEntities(items.map((it) => ({ kind: 'entity', ...it })));
                })
                .catch(() => { if (id === reqId.current) setEntities([]); })
                .finally(() => { if (id === reqId.current) setLoading(false); });
        }, 250);
        return () => clearTimeout(timer);
    }, [q, open]);

    // 结果:直接命中的页面 → 实体 → 模糊命中的页面;纯 id / IP / 邮箱 / 域名类查询实体排最前
    const entityFirst = /^#?\d+$/.test(query) || /[@.:]/.test(query);
    const results = useMemo(() => {
        const direct = pages.filter((p) => p.direct);
        const fuzzy = pages.filter((p) => !p.direct);
        return entityFirst ? [...entities, ...direct, ...fuzzy] : [...direct, ...entities, ...fuzzy];
    }, [entityFirst, entities, pages]);

    useEffect(() => { setActive(0); }, [query]);

    useEffect(() => {
        const el = listRef.current && listRef.current.querySelector(`[data-idx="${active}"]`);
        if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
    }, [active]);

    const go = (item) => {
        if (!item) return;
        onClose();
        navigate(adminPath(item.path));
    };

    const onKeyDown = (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActive((i) => Math.min(i + 1, results.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActive((i) => Math.max(i - 1, 0));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            go(results[active]);
        }
    };

    const renderRow = (item, idx) => {
        const isActive = idx === active;
        return (
            <div
                key={`${item.kind}-${item.type || ''}-${item.path}-${item.title}`}
                data-idx={idx}
                onMouseEnter={() => setActive(idx)}
                onClick={() => go(item)}
                style={{
                    display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', cursor: 'pointer',
                    borderRadius: token.borderRadius,
                    background: isActive ? token.controlItemBgActive : 'transparent',
                }}
            >
                <Tag style={{ marginInlineEnd: 0, minWidth: 64, textAlign: 'center' }} color={item.kind === 'page' ? 'blue' : undefined}>
                    {item.kind === 'page' ? '页面' : (TYPE_LABEL[item.type] || item.type)}
                </Tag>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <Text ellipsis style={{ display: 'block' }}>{item.title}</Text>
                    {item.sub ? <Text type="secondary" ellipsis style={{ display: 'block', fontSize: 12 }}>{item.sub}</Text> : null}
                </div>
                {item.status ? <Tag color={STATUS_COLOR(item.status)} style={{ marginInlineEnd: 0 }}>{item.status}</Tag> : null}
                {isActive ? <EnterOutlined style={{ color: token.colorTextTertiary }} /> : null}
            </div>
        );
    };

    return (
        <Modal
            open={open}
            onCancel={onClose}
            footer={null}
            closable={false}
            width={640}
            style={{ top: 80 }}
            styles={{ content: { padding: 12 } }}
            destroyOnClose
        >
            <Input
                ref={inputRef}
                size="large"
                allowClear
                prefix={<SearchOutlined />}
                suffix={loading ? <Spin size="small" /> : null}
                placeholder="页面 / 用户 / 域名 / IP / #账单号 …"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={onKeyDown}
            />
            <div ref={listRef} style={{ maxHeight: '60vh', overflowY: 'auto', marginTop: 8 }}>
                {results.map(renderRow)}
                {!results.length && (
                    <div style={{ padding: 24, textAlign: 'center' }}>
                        <Text type="secondary">{query ? (loading ? '搜索中…' : '没有结果') : 'Ctrl / ⌘ + K'}</Text>
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default CommandPalette;
